import { useEffect, useRef } from "react";
import { SPINESIM_MODALITIES } from "../../../classes/enums/MedicalImagesEnum";
import { SurgerySidebarCategories } from "../../../classes/enums/SurgicalActsEnum";
import { useAppContext } from "../../../classes/contexts/AppContext";
import CheckSpp from "../spp/CheckSppWidget";
import RadioDensityStatistics from "../radioDensity/RadioDensityStatistics";

/**
 * Sidebar component for Check module
 * Manages displaying CheckSpp or RadioDensityStatistics based on active state
 */
const CheckSidebar = ({
  activeSidebar,
  isRadioDensityActive,
  dataBundle,
  selectedModality,
  onSppSelected,
  onSppSelectedCustom,
  activeParameters,
  removeCustomParameter,
  instances,
  validationRefresh,
  selectedTargetType,
  onTargetTypeChange,
  digitalTwinId,
  initialKeyParameters,
  originalMeasurements,
  radioDensityStatistics,
  onRadioDensityStatisticsUpdate,
  areViewsReady,
}) => {
  const { showOnlyRightSidebar, hideSidebars } = useAppContext();

  // Ref to track if CheckSpp component is currently mounted
  // Prevents unnecessary re-mounts when only data updates (e.g., adding first custom measurement)
  const checkSppMountedRef = useRef(false);
  const prevCustomMeasurementCountRef = useRef(
    dataBundle.customMeasurementConfigsFrontEnd?.length ?? 0
  );

  useEffect(() => {
    const previousCount = prevCustomMeasurementCountRef.current;
    const currentCount =
      dataBundle.customMeasurementConfigsFrontEnd?.length ?? 0;
    const shouldSkipFirstCustomMeasurementRefresh =
      previousCount === 0 &&
      currentCount === 1 &&
      checkSppMountedRef.current &&
      activeSidebar === SurgerySidebarCategories.spp;

    prevCustomMeasurementCountRef.current = currentCount;

    if (shouldSkipFirstCustomMeasurementRefresh) {
      return;
    }

    const shouldShowCheckSpp =
      activeSidebar === SurgerySidebarCategories.spp &&
      dataBundle.annotations &&
      dataBundle.measurementConfigsFrontEnd;

    if (shouldShowCheckSpp) {
      // Determine modalityContext based on selectedModality
      let modalityContext = null;
      if (selectedModality === SPINESIM_MODALITIES.DX) {
        modalityContext = SPINESIM_MODALITIES.DX;
      } else if (selectedModality === SPINESIM_MODALITIES.CT) {
        modalityContext = SPINESIM_MODALITIES.CT;
      }

      // Always call showOnlyRightSidebar to ensure props are updated,
      // but React will handle the reconciliation and avoid re-mounting
      // the component if the structure is the same
      showOnlyRightSidebar(
        <CheckSpp
          measurementConfigs={dataBundle.measurementConfigsFrontEnd}
          customMeasurementConfigs={dataBundle.customMeasurementConfigsFrontEnd}
          onSppSelected={onSppSelected}
          onSppSelectedCustom={onSppSelectedCustom}
          activeParameters={activeParameters}
          removeCustomParameter={removeCustomParameter}
          instances={instances}
          refresh={validationRefresh}
          selectedTargetType={selectedTargetType}
          onTargetTypeChange={onTargetTypeChange}
          digitalTwinId={digitalTwinId}
          initialKeyParameters={initialKeyParameters}
          modalityContext={modalityContext}
          originalMeasurements={originalMeasurements}
        />
      );
      checkSppMountedRef.current = true;
    } else if (isRadioDensityActive) {
      checkSppMountedRef.current = false;
      showOnlyRightSidebar(
        <RadioDensityStatistics
          annotations={dataBundle.organizedAnnotations}
          areViewsReady={areViewsReady}
          instances={instances}
          refresh={validationRefresh}
          externalStatistics={radioDensityStatistics}
          onStatisticsUpdate={onRadioDensityStatisticsUpdate}
        />
      );
    } else {
      checkSppMountedRef.current = false;
      hideSidebars();
    }
  }, [
    activeSidebar,
    isRadioDensityActive,
    dataBundle.annotations,
    dataBundle.customMeasurementConfigsFrontEnd,
    dataBundle.measurementConfigsFrontEnd,
    showOnlyRightSidebar,
    hideSidebars,
    onSppSelected,
    onSppSelectedCustom,
    activeParameters,
    removeCustomParameter,
    instances,
    validationRefresh,
    selectedTargetType,
    onTargetTypeChange,
    digitalTwinId,
    initialKeyParameters,
    dataBundle.organizedAnnotations,
    areViewsReady,
    radioDensityStatistics,
    onRadioDensityStatisticsUpdate,
    selectedModality,
    originalMeasurements,
  ]);

  // This component doesn't render anything directly
  // It manages the sidebar through the AppContext
  return null;
};

export default CheckSidebar;
